import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { recentApi } from "@/api/ux";
import {
  RECENTLY_VIEWED_ICON_MAP,
  RECENTLY_VIEWED_ROUTE_MAP,
} from "@/components/ux/RecentlyViewedWidget";
import { Timestamp } from "@/design-system/components/ui";
import { WidgetFrame } from "./WidgetFrame";

/**
 * Light-canvas variant of the sidebar's Recently Viewed for the dashboard's
 * personal band. Shares the sidebar widget's query key, route map, and icon
 * map — one data source, two skins.
 */
export function RecentlyViewedPanel() {
  const recent = useQuery({
    queryKey: ["recent"],
    queryFn: () => recentApi.list(),
  });

  const items = (recent.data ?? []).slice(0, 5);

  return (
    <WidgetFrame
      title="Recently viewed"
      isLoading={recent.isLoading}
      isError={recent.isError}
      isEmpty={!recent.isLoading && !recent.isError && items.length === 0}
      emptyText="Pages you open will collect here for quick return trips."
    >
      <ul className="m-0 p-0 list-none divide-y divide-border-light">
        {items.map((item) => {
          const Icon = RECENTLY_VIEWED_ICON_MAP[item.entityType] ?? FileText;
          const route =
            RECENTLY_VIEWED_ROUTE_MAP[item.entityType]?.(item.entityId) ??
            `/${item.entityType.toLowerCase()}/${item.entityId}`;
          return (
            <li key={`${item.entityType}-${item.entityId}`}>
              <Link
                to={route}
                className="flex items-center gap-space-2 py-space-2 hover:bg-white-warm -mx-space-2 px-space-2 rounded-sm"
              >
                <Icon size={14} strokeWidth={1.5} className="text-gold-shadow shrink-0" aria-hidden />
                <span className="flex-1 truncate text-small text-onyx">{item.entityTitle}</span>
                <Timestamp value={item.viewedAt} className="shrink-0" />
              </Link>
            </li>
          );
        })}
      </ul>
    </WidgetFrame>
  );
}
